// Find its Wise transfer (Mitch, 2026-11-03: "I added a cost in costs & invoices, and there's no clear way to go and
// search/pick a wise transaction we already did"): from a run cost or a bill, the Wise transfers HHL Ops knows (Wise
// history: read from Wise or a statement), the likeliest first: the one Wise history suggests for it, then the same
// amount, the same vendor, the nearest day. Picking one opens Choose… for that transfer with this already ticked, so
// everything Choose… knows (part payments, rounding, a bank charge, other things the transfer paid) works the same and
// Link is one click when it's exact. The other way round is still Wise history → Choose….
import { useMemo, useState } from "react";
import { type MatchTargetKind, type Suggestion, transferLike, transferVendor } from "../../../../shared/bills.ts";
import type { WiseTransfer } from "../../../../shared/db.gen.ts";
import { amount, useBillIndex } from "../../lib/bills.ts";
import { shortDate } from "../../lib/costs.ts";
import { useRows } from "../../lib/sync.ts";
import { Badge, Input } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { toast } from "../../ui/toast.tsx";
import { ChooseDialog } from "./ChooseDialog.tsx";
import { Dialog } from "./PayDialogs.tsx";

/** What's being paid: a run cost or a bill, with what the picker ranks transfers by. */
export type PayTarget = {
  kind: Extract<MatchTargetKind, "production_cost" | "bill">;
  id: string;
  vendorId: string | null;
  amount: number;
  currency: string;
  /** Its invoice's day, else when it was paid: transfers near it rank higher. */
  date: string | null;
  /** "PR-002 cost: Freight", "BL-1004": the dialog's title. */
  label: string;
};

type Row = WiseTransfer & { vendorId: string | null; suggested: boolean; sameAmount: boolean; days: number | null; score: number };

const cents = (n: number) => Math.round(Number(n) * 100);
const daysBetween = (a: string | null, b: string | null) => (a && b ? Math.abs(Date.parse(a) - Date.parse(b)) / 86_400_000 : null);

export function WisePicker({
  target,
  onClose,
  beforePick,
  note,
}: {
  target: PayTarget;
  onClose: () => void;
  /** Before Choose… opens for the transfer picked (a run cost records it as paid by it); false stops there. */
  beforePick?: (t: WiseTransfer) => Promise<boolean> | boolean;
  /** A line under the title (a run cost already said to be paid another way). */
  note?: string;
}) {
  const transfers = useRows("wise_transfer");
  const payments = useRows("payment");
  const ix = useBillIndex();
  const [query, setQuery] = useState("");
  const [all, setAll] = useState(false); // also the ones already linked or set aside
  const [choosing, setChoosing] = useState<WiseTransfer | null>(null);
  const rows = useMemo<Row[]>(
    () =>
      transfers
        .filter((t) => all || (!t.payment_id && t.match_status !== "ignored"))
        .map((t) => {
          const vendorId = transferVendor(ix, transferLike(t))?.vendorId ?? null;
          const sugs = (Array.isArray(t.suggestions) ? t.suggestions : []) as Suggestion[];
          const suggested = sugs.some((s) => s.targets.some((x) => x.id === target.id));
          const sameAmount = t.currency.toUpperCase() === target.currency.toUpperCase() && cents(t.amount) === cents(target.amount);
          const days = daysBetween(t.transfer_date ? String(t.transfer_date).slice(0, 10) : null, target.date);
          const sameVendor = !!target.vendorId && vendorId === target.vendorId;
          // The likeliest first: Wise history's own suggestion, the same amount, the same vendor, then the nearest day.
          const score = (suggested ? 8 : 0) + (sameAmount ? 4 : 0) + (sameVendor ? 2 : 0) + (days != null ? Math.max(0, 1 - days / 120) : 0);
          return { ...t, vendorId, suggested, sameAmount, days, score };
        }),
    [transfers, all, ix, target],
  );
  const vendorName = (id: string | null) => {
    const v = id ? ix.d.vendors.find((x) => x.id === id) : undefined;
    return v ? v.short_name || v.name : "";
  };
  const pick = async (t: Row) => {
    if (t.payment_id) {
      const p = payments.find((x) => x.id === t.payment_id);
      return toast.info(`Transfer ${t.wise_transfer_id} is linked already${p ? ` (${p.number})` : ""}: undo it in Wise history to link it again`);
    }
    if (t.match_status === "ignored") return toast.info(`Transfer ${t.wise_transfer_id} is set aside as not a bill: show it again in Wise history first`);
    if (beforePick && !(await beforePick(t))) return;
    setChoosing(t);
  };
  const columns: Column<Row>[] = [
    {
      key: "transfer_date",
      header: "Date",
      width: "90px",
      value: (t) => (t.transfer_date ? String(t.transfer_date) : ""),
      cell: (t) => (t.transfer_date ? shortDate(String(t.transfer_date).slice(0, 10)) : "—"), // its day as Wise dated it (as Wise history shows it)
    },
    { key: "payee_name", header: "Paid to", width: "minmax(140px,1.4fr)", cell: (t) => <span className="truncate">{t.payee_name || "—"}</span> },
    {
      key: "vendor",
      header: "Vendor",
      width: "minmax(90px,1fr)",
      optional: true,
      value: (t) => vendorName(t.vendorId),
    },
    {
      key: "reference",
      header: "Reference",
      width: "minmax(120px,1.2fr)",
      cell: (t) => <span className="truncate font-mono text-[12px]">{t.reference || "—"}</span>,
    },
    {
      key: "amount",
      header: "Amount",
      width: "120px",
      align: "right",
      value: (t) => Number(t.amount),
      text: (t) => `${t.amount} ${t.currency}`,
      cell: (t) => <span className={t.sameAmount ? "font-mono font-semibold" : "font-mono"}>{amount(Number(t.amount), t.currency)}</span>,
    },
    {
      key: "why",
      header: "Why it's here",
      width: "minmax(150px,1fr)",
      value: (t) => t.score,
      text: (t) =>
        [t.suggested && "Suggested", t.sameAmount && "Same amount", t.vendorId && t.vendorId === target.vendorId && "Same vendor"].filter(Boolean).join(", "),
      cell: (t) => (
        <span className="flex min-w-0 items-center gap-1">
          {t.suggested && <Badge tone="ok">Suggested</Badge>}
          {t.sameAmount && <Badge tone="accent">Same amount</Badge>}
          {!!target.vendorId && t.vendorId === target.vendorId && <Badge>Same vendor</Badge>}
        </span>
      ),
    },
    {
      key: "state",
      header: "Status",
      width: "110px",
      value: (t) => (t.payment_id ? 2 : t.match_status === "ignored" ? 3 : 0),
      text: (t) => (t.payment_id ? "Linked" : t.match_status === "ignored" ? "Not a bill" : "Not linked"),
      cell: (t) =>
        t.payment_id ? (
          <Badge tone="ok">Linked</Badge>
        ) : t.match_status === "ignored" ? (
          <Badge>Not a bill</Badge>
        ) : (
          <span className="text-muted">Not linked</span>
        ),
    },
    {
      key: "wise_transfer_id",
      header: "Transfer no.",
      width: "110px",
      optional: true,
      cell: (t) => <span className="font-mono text-[12px]">{t.wise_transfer_id}</span>,
    },
  ];
  if (choosing)
    return (
      <ChooseDialog
        t={choosing}
        vendorId={target.vendorId ?? undefined}
        prepick={{ kind: target.kind, id: target.id }}
        onClose={() => {
          setChoosing(null);
          onClose();
        }}
      />
    );
  return (
    <Dialog
      title={`Which Wise transfer paid ${target.label}?`}
      sub={
        <>
          <span className="font-mono">{amount(target.amount, target.currency)}</span>
          {target.vendorId ? ` to ${vendorName(target.vendorId)}` : ""}
          {target.date ? ` · ${shortDate(target.date)}` : ""}. Pick it: Choose… opens with this ticked, to add anything else it paid.
          {note && <div className="mt-0.5 text-warn">{note}</div>}
        </>
      }
      onClose={onClose}
      width={900}
    >
      <div className="mt-3 flex items-center gap-2">
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Payee, reference, amount or transfer no."
          aria-label="Find a transfer"
          className="h-7 w-72"
        />
        <label className="flex items-center gap-1.5 text-[12px] text-muted">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          Also linked and set-aside ones
        </label>
      </div>
      <div className="mt-2">
        <DataTable
          fit={14}
          rows={rows}
          columns={columns}
          search={query}
          defaultSort={{ key: "why", desc: true }}
          onOpen={pick}
          exportName="Find a Wise transfer"
          pageFilters={false}
          rowNoun={["transfer", "transfers"]}
          empty={
            transfers.length
              ? "No transfer matches: try another word, or show the linked ones too."
              : "No Wise transfers here yet: import them on Bills & payments → Wise history (from Wise, or a statement CSV)."
          }
        />
      </div>
    </Dialog>
  );
}
